// node_modules
import React, { useEffect, useContext, useState } from "react";
import { Button } from "primereact/button";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

// redux
import { useLogoutMutation } from "../redux/services/auth";
import { useGetMeQuery } from "../redux/services/user";
import {
  setLoading,
  setShowMobileSidebar,
  setShowCustomerMenu,
} from "../redux/slices/app.slice";

// context
import { ToastContext } from "../App";

// styles
import * as styles from "./Header.module.css";

// img
import logo from "../../public/images/logo.svg";

import CustomerMenu from "./CustomerMenu/CustomerMenu";

// utils
import getSubdomain from "../utils/getSubdomain";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const showToast = useContext(ToastContext);

  const orders = useSelector((state) => state.orders.orders);
  const openedStations = useSelector((state) => state.stations.openedStations);
  const sidebarVisible = useSelector((state) => state.app.sidebarVisible);
  const showMobileSidebar = useSelector((state) => state.app.showMobileSidebar);
  const showCustomerMenu = useSelector((state) => state.app.showCustomerMenu);

  const isCustomerProfilePage =
    location.pathname === "/account" || location.pathname === "/settings";

  const isCustomerPage =
    location.pathname === "/" ||
    location.pathname === "/order-status" ||
    location.pathname.startsWith("/make-order") ||
    isCustomerProfilePage;

  const isLoggedIn = !!localStorage.getItem("access_token");
  const subdomain = getSubdomain();
  const isMainDomain =
    subdomain.startsWith("localhost") ||
    subdomain === "dwtl" ||
    subdomain === "deltaspark";
  const showBurger =
    !isMainDomain &&
    (location.pathname === "/" ||
      location.pathname === "/order-status" ||
      location.pathname.startsWith("/make-order") ||
      (location.pathname === "/student/login" && isLoggedIn) ||
      isCustomerProfilePage);

  const toggleCustomerMenu = () => {
    dispatch(setShowCustomerMenu(!showCustomerMenu));
  };

  const [
    logout,
    { isSuccess: isLogoutSuccess, isError: isLogoutError, error: logoutError },
  ] = useLogoutMutation();

  const profile = useSelector((state) => state.user.user);

  const handleLogout = () => {
    logout();
  };

  useEffect(() => {
    dispatch(setLoading(false));
  }, []);

  useEffect(() => {
    if (isLogoutError) {
      showToast("error", logoutError?.status, logoutError?.data?.message);
    }
  }, [isLogoutError]);

  useEffect(() => {
    if (isLogoutSuccess) {
      showToast("success", "Success", "Logged out successfully");
      const isStudentPath = !location.pathname.startsWith("/app/");
      navigate(isStudentPath ? "/student/login" : "/login");
    }
  }, [isLogoutSuccess, profile]);

  const toggleSidebar = () => {
    dispatch(setShowMobileSidebar(!showMobileSidebar));
  };

  const logoLink = () => {
    if (location.pathname === "/login") {
      return "/login";
    }

    switch (profile?.role) {
      case "admin":
        return "/app/admin";
      case "manager":
        return "/app/manager";
      case "chef":
        return "/app/chef";
      default:
        return "/";
    }
  };

  if (location.pathname.endsWith("/status")) {
    return null;
  }
  const [offlineBannerVisible, setOfflineBannerVisible] = useState(false);

  useEffect(() => {
    const handler = (e) => setOfflineBannerVisible(Boolean(e.detail?.visible));
    window.addEventListener("offlineBanner", handler);
    return () => window.removeEventListener("offlineBanner", handler);
  }, []);

  const headerInlineStyle = {
    transform: offlineBannerVisible ? "translateY(28px)" : "translateY(0)",
    transition: "transform 240ms cubic-bezier(0.22, 1, 0.36, 1)",
  };

  return (
    <header className={`${styles.header}`} style={headerInlineStyle}>
      <div
        className={
          location.pathname === "/" ||
          location.pathname === "/order-status" ||
          location.pathname.startsWith("/make-order") ||
          isCustomerProfilePage
            ? styles.containerStudent
            : styles.container
        }
      >
        {!sidebarVisible &&
          (location.pathname.startsWith("/app/manager") ||
            location.pathname.startsWith("/app/admin")) && (
            <Button
              icon={`pi ${showMobileSidebar ? "pi-times" : "pi-align-justify"}`}
              rounded
              text
              severity="secondary"
              onClick={toggleSidebar}
              className={styles.sidebarBtn}
            />
          )}

        <Link to={logoLink()}>
          <img className={styles.logo} src={logo} alt="" />
        </Link>

        <>
          {!showBurger && openedStations?.length !== 0 && orders?.length && (
            <Link to="/order-status" className={styles.orderStatusLink}>
              <i className="pi pi-clock"></i>
              <div>MY ORDERS</div>
            </Link>
          )}
          {!showBurger &&
            localStorage.getItem("access_token") &&
            sidebarVisible && (
              <Button
                label="LOG OUT"
                icon="pi pi-sign-out"
                className={`p-button-outlined ${styles.logoutButton}`}
                onClick={handleLogout}
              />
            )}
        </>
      </div>
      {showBurger && (
        <button
          type="button"
          className={`${styles.burgerBtn} ${
            showCustomerMenu ? styles.burgerOpen : ""
          }`}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            toggleCustomerMenu();
          }}
          aria-label={showCustomerMenu ? "Close menu" : "Open menu"}
          aria-expanded={showCustomerMenu}
        >
          <span className={styles.burgerBar} />
          <span className={styles.burgerBar} />
          <span className={styles.burgerBar} />
        </button>
      )}
      {showBurger && <CustomerMenu />}
    </header>
  );
};

export default Header;
